import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
  fetchMediaLibrary,
  transitionWorkflow,
  saveStudioSearch,
  fetchSavedSearches,
  linkChannelAsset,
  fetchComments,
  addComment,
  fetchActivity,
  bulkWorkflowTransition,
  findDuplicateAssets,
  fetchAssetVersions,
  WORKFLOW_STATUSES,
} from "./contentStudioApi";
import { runtimeConfig } from "../../config/runtimeConfig";
import { fetchStudioCapabilities } from "../Helper/studioCapabilities";
import "./ContentStudio.css";

function adminVideosCreateUrl(scope) {
  const base = (runtimeConfig.adminBaseUrl || "").replace(/\/+$/, "") + "/videos/create";
  const parts = [];
  if (scope && scope.creator_id) {
    parts.push("creator_id=" + encodeURIComponent(scope.creator_id));
  }
  if (scope && scope.organization_id) {
    parts.push("organization_id=" + encodeURIComponent(scope.organization_id));
  }
  return parts.length ? base + "?" + parts.join("&") : base;
}

function adminVideosEditUrl(adminVideoId) {
  const base = (runtimeConfig.adminBaseUrl || "").replace(/\/+$/, "");
  return base + "/videos/edit/?admin_video_id=" + encodeURIComponent(adminVideoId);
}

// Admin video tools live in Blade Admin; staff moderators also carry is_platform_admin,
// so require the server-issued administrator / sub_admin portal as well.
function canUseAdminVideoTools(caps) {
  const portals = caps && Array.isArray(caps.portals) ? caps.portals : [];
  return (
    !!(caps && caps.is_platform_admin) &&
    portals.some((p) => ["administrator", "sub_admin"].includes(String(p.key || p.role)))
  );
}

function MediaLibraryPage(props) {
  const params =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : null;
  const qsCreator = params ? params.get("creator_id") : null;
  const qsOrg = params ? params.get("organization_id") : null;

  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [origin, setOrigin] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [searches, setSearches] = useState([]);
  const [selected, setSelected] = useState(null);
  const [comments, setComments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [versions, setVersions] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [note, setNote] = useState("");
  const [channelId, setChannelId] = useState("");
  const [loading, setLoading] = useState(true);
  const [studioLinks, setStudioLinks] = useState({ creator: null, org: null });
  const [scope, setScope] = useState({
    creator_id: qsCreator || null,
    organization_id: qsOrg || null,
  });
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [adminVideoTools, setAdminVideoTools] = useState(false);
  const [scopeReady, setScopeReady] = useState(false);
  const [scopeError, setScopeError] = useState("");

  const load = async () => {
    if (!scopeReady) return;
    if (!isPlatformAdmin && !scope.creator_id && !scope.organization_id) {
      setItems([]);
      setTotal(0);
      setScopeError(
        "Select a Creator or Organization workspace before opening the Media Library."
      );
      setLoading(false);
      return;
    }
    setScopeError("");
    setLoading(true);
    const data = await fetchMediaLibrary({
      q: q || undefined,
      workflow_status: status || undefined,
      origin: origin || undefined,
      limit: 60,
      creator_id: scope.creator_id || undefined,
      organization_id: scope.organization_id || undefined,
    });
    if (data && data.error) {
      setScopeError(data.error || "Unable to load media library");
      setItems([]);
      setTotal(0);
    } else {
      setItems((data && data.items) || []);
      setTotal((data && data.total) || 0);
    }
    setSearches(await fetchSavedSearches({}));
    setLoading(false);
  };

  useEffect(() => {
    fetchStudioCapabilities().then((caps) => {
      let creatorId = qsCreator;
      let orgId = qsOrg;
      const isAdmin = !!(caps && caps.is_platform_admin);

      // Platform admins stay global unless URL explicitly scopes (workspace Media
      // links may pass creator_id/organization_id intentionally).
      if (!isAdmin) {
        try {
          const raw = localStorage.getItem("tpg_active_workspace");
          const stored = raw ? JSON.parse(raw) : null;
          if (stored && stored.entityId) {
            if (stored.workspaceId === "creator" && !creatorId) {
              creatorId = String(stored.entityId);
            }
            if (stored.workspaceId === "organization" && !orgId) {
              orgId = String(stored.entityId);
            }
          }
        } catch (e) {}

        // Prefer single assigned entity when no explicit scope.
        if (!creatorId && !orgId && caps) {
          if (caps.creator_ids && caps.creator_ids.length === 1) {
            creatorId = String(caps.creator_ids[0]);
          } else if (caps.organization_ids && caps.organization_ids.length === 1) {
            orgId = String(caps.organization_ids[0]);
          }
        }
      }

      setIsPlatformAdmin(isAdmin);
      setAdminVideoTools(canUseAdminVideoTools(caps));
      setScope({
        creator_id: creatorId || null,
        organization_id: orgId || null,
      });
      setScopeReady(true);

      const creator =
        creatorId ||
        (caps && caps.creator_ids && caps.creator_ids.length
          ? String(caps.creator_ids[0])
          : null);
      const org =
        orgId ||
        (caps && caps.organization_ids && caps.organization_ids.length
          ? String(caps.organization_ids[0])
          : null);
      setStudioLinks({
        creator: creator ? "/workspace/creator/" + creator : null,
        org: org ? "/workspace/organization/" + org : null,
      });
    });
  }, []);

  useEffect(() => {
    load();
  }, [scope.creator_id, scope.organization_id, scopeReady, isPlatformAdmin]);

  const openDetail = async (item) => {
    setSelected(item);
    setComments(await fetchComments(item.admin_video_id));
    setActivity(await fetchActivity(item.admin_video_id));
    setVersions(await fetchAssetVersions(item.admin_video_id));
  };

  const toggleSelect = (id) => {
    const sid = String(id);
    setSelectedIds((prev) =>
      prev.indexOf(sid) >= 0 ? prev.filter((x) => x !== sid) : prev.concat([sid])
    );
  };

  const onBulk = async (nextStatus) => {
    if (!selectedIds.length) return;
    await bulkWorkflowTransition({
      admin_video_ids: selectedIds,
      status: nextStatus,
    });
    setSelectedIds([]);
    load();
  };

  const onScanDupes = async () => {
    setDuplicates(await findDuplicateAssets({}));
  };

  return (
    <div className="cstudio">
      <Helmet>
        <title>Media Library · TPG</title>
      </Helmet>
      <header className="cstudio__header">
        <div>
          <p className="cstudio-eyebrow">Unified Media Library</p>
          <h1>Media Assets</h1>
          <p className="cstudio-muted">
            {scope.creator_id
              ? "Scoped to creator #" + scope.creator_id
              : scope.organization_id
                ? "Scoped to organization #" + scope.organization_id
                : isPlatformAdmin
                  ? "Platform catalog (global editorial access)."
                  : "Permission-scoped for your account."}
          </p>
          {scopeError ? <p className="cstudio-error">{scopeError}</p> : null}
        </div>
        <nav className="cstudio__nav">
          <Link to="/studio">Studio home</Link>
          <Link to="/studio/timeline">Schedule</Link>
          {studioLinks.creator ? (
            <Link to={studioLinks.creator}>Creator Studio</Link>
          ) : null}
          {studioLinks.org ? (
            <Link to={studioLinks.org}>Org Studio</Link>
          ) : null}
          {adminVideoTools ? (
            <a
              href={adminVideosCreateUrl(scope)}
              target="_blank"
              rel="noopener noreferrer"
            >
              Upload Video
            </a>
          ) : null}
        </nav>
      </header>

      <div className="cstudio-toolbar">
        <input
          type="search"
          placeholder="Search title or description"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {WORKFLOW_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select value={origin} onChange={(e) => setOrigin(e.target.value)}>
          <option value="">All origins</option>
          <option value="creator">Creator</option>
          <option value="organization">Organization</option>
          <option value="platform">Platform</option>
        </select>
        <button type="button" className="cstudio-btn" onClick={load}>
          Search
        </button>
        <button type="button" className="cstudio-btn cstudio-btn--ghost" onClick={onScanDupes}>
          Find duplicates
        </button>
        <button
          type="button"
          className="cstudio-btn cstudio-btn--ghost"
          onClick={() =>
            saveStudioSearch({
              name: q || status || origin || "Library filter",
              query: { q, workflow_status: status, origin },
            }).then(() => fetchSavedSearches({}).then(setSearches))
          }
        >
          Save search
        </button>
      </div>

      {selectedIds.length > 0 && (
        <div className="cstudio-toolbar">
          <span className="cstudio-muted">{selectedIds.length} selected</span>
          {["in_review", "approved", "published", "archived"].map((s) => (
            <button key={s} type="button" className="cstudio-btn cstudio-btn--ghost" onClick={() => onBulk(s)}>
              Bulk → {s}
            </button>
          ))}
        </div>
      )}

      {!!duplicates.length && (
        <section className="cstudio-panel">
          <h2>Duplicate title groups ({duplicates.length})</h2>
          <ul className="cstudio-list">
            {duplicates.slice(0, 20).map((g) => (
              <li key={g.title}>
                <div>
                  <strong>{g.title}</strong>
                  <div className="cstudio-muted">
                    {(g.items || []).map((i) => "#" + i.admin_video_id).join(", ")}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
      {!!searches.length && (
        <p className="cstudio-muted">
          Saved:{" "}
          {searches.map((s) => (
            <button
              key={s.id}
              type="button"
              className="cstudio-btn cstudio-btn--ghost"
              onClick={() => {
                const query = s.query || {};
                setQ(query.q || "");
                setStatus(query.workflow_status || "");
                setOrigin(query.origin || "");
              }}
            >
              {s.name}
            </button>
          ))}
        </p>
      )}

      <p className="cstudio-muted">
        {loading ? "Loading…" : total + " assets"} · One Media Asset can air on many channels
        without duplication.
      </p>

      <div className="cstudio-panel">
        <ul className="cstudio-list">
          {items.map((item) => (
            <li key={item.media_asset_key || item.id}>
              <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={selectedIds.indexOf(String(item.admin_video_id)) >= 0}
                  onChange={() => toggleSelect(item.admin_video_id)}
                />
                <button type="button" className="cstudio-btn cstudio-btn--ghost" onClick={() => openDetail(item)}>
                  <strong>{item.title || "Untitled"}</strong>
                  <div className="cstudio-muted">
                    {(item.ownership && item.ownership.origin) || "—"} · {item.presentationKind}
                  </div>
                </button>
              </div>
              <span
                className={
                  "cstudio-badge cstudio-badge--" +
                  ((item.workflow && item.workflow.status) || "draft")
                }
              >
                {(item.workflow && item.workflow.status) || "draft"}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {selected && (
        <section className="cstudio-panel">
          <h2>{selected.title}</h2>
          <p className="cstudio-muted">{selected.media_asset_key}</p>
          {adminVideoTools && selected.admin_video_id ? (
            <p>
              <a
                className="cstudio-btn"
                href={adminVideosEditUrl(selected.admin_video_id)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Edit video
              </a>
            </p>
          ) : null}
          <div className="cstudio-workflow">
            {WORKFLOW_STATUSES.map((s) => (
              <button
                key={s}
                type="button"
                className="cstudio-btn cstudio-btn--ghost"
                onClick={() =>
                  transitionWorkflow({
                    admin_video_id: selected.admin_video_id,
                    status: s,
                  }).then(load)
                }
              >
                {s}
              </button>
            ))}
          </div>
          <div className="cstudio-toolbar" style={{ marginTop: "0.75rem" }}>
            <input
              placeholder="Channel id to feature/link"
              value={channelId}
              onChange={(e) => setChannelId(e.target.value)}
            />
            <button
              type="button"
              className="cstudio-btn"
              onClick={() =>
                linkChannelAsset({
                  channel_id: channelId,
                  admin_video_id: selected.admin_video_id,
                  media_asset_key: selected.media_asset_key,
                  is_featured: true,
                  link_type: "featured",
                })
              }
            >
              Link to channel
            </button>
          </div>
          <h3>Collaboration</h3>
          <ul className="cstudio-list">
            {comments.map((c) => (
              <li key={c.id}>
                <div>
                  <strong>{c.comment_type}</strong>
                  <div>{c.body}</div>
                </div>
              </li>
            ))}
          </ul>
          <div className="cstudio-toolbar">
            <input
              placeholder="Internal note / review request"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <button
              type="button"
              className="cstudio-btn cstudio-btn--ghost"
              onClick={() =>
                addComment({
                  admin_video_id: selected.admin_video_id,
                  body: note,
                  comment_type: "note",
                  is_internal: true,
                }).then(async () => {
                  setNote("");
                  setComments(await fetchComments(selected.admin_video_id));
                })
              }
            >
              Add note
            </button>
          </div>
          <h3>Activity</h3>
          <ul className="cstudio-list">
            {activity.map((a) => (
              <li key={a.id}>
                <span>{a.action}</span>
                <span className="cstudio-muted">{a.created_at}</span>
              </li>
            ))}
          </ul>
          <h3>Versions</h3>
          <ul className="cstudio-list">
            {versions.map((v) => (
              <li key={v.admin_video_id || v.id}>
                <span>
                  v{v.version_number || 1} · {v.title}
                </span>
                <span className="cstudio-badge">
                  {(v.workflow && v.workflow.status) || "—"}
                </span>
              </li>
            ))}
            {!versions.length && <li className="cstudio-muted">Single version</li>}
          </ul>
        </section>
      )}
    </div>
  );
}

export default MediaLibraryPage;
